import { ChallengeBikeType, ChallengeBoardKind, ChallengeBoardRow, ChallengeMe, ChallengeRide, ChallengeSummary, ChallengeTeam, User } from './types';
import { COUNTRY_LABELS, CountryCode, displayBundesland } from './brand';
import { formatDayMonthLong, formatMonthLong, formatDayShort } from './dateFormat';

/**
 * Reine Logik der Winterchallenge, gemeinsam für Web und App (`docs/bereiche/winterchallenge.md`).
 * Gerechnet wird im Backend; hier steht nur, was aus einer Antwort für die Anzeige folgt.
 */

export const CHALLENGE_BIKE_TYPES: ChallengeBikeType[] = ['ROAD', 'GRAVEL', 'MTB', 'TOUR', 'INDOOR'];

export const CHALLENGE_BIKE_LABELS: Record<ChallengeBikeType, string> = {
  ROAD: 'Rennrad',
  GRAVEL: 'Gravel',
  MTB: 'MTB',
  TOUR: 'Touren / Alltag',
  INDOOR: 'Rolle/Indoor',
};

/** Persönliche Meilensteine in Kilometern. */
export const CHALLENGE_MILESTONES = [250, 500, 1000, 2500, 5000];

/** Höchstens so viele Kilometer je Tag, Summe aller Einträge. */
export const CHALLENGE_MAX_KM_PER_DAY = 400;

/** Der nächste Meilenstein über `km`, oder `null`, wenn alle erreicht sind. */
export function nextMilestone(km: number): { target: number; missing: number; previous: number } | null {
  let previous = 0;
  for (const target of CHALLENGE_MILESTONES) {
    if (km < target) return { target, missing: target - km, previous };
    previous = target;
  }
  return null;
}

/** Erreichte Meilensteine, aufsteigend. */
export function reachedMilestones(km: number): number[] {
  return CHALLENGE_MILESTONES.filter(m => km >= m);
}

/** Der höchste Meilenstein, den der Schritt von `before` auf `after` Kilometer überquert, sonst `null`. */
export function crossedMilestone(before: number, after: number): number | null {
  const fresh = reachedMilestones(after).filter(m => before < m);
  return fresh.length ? fresh[fresh.length - 1] : null;
}

/** Ein Schnipsel der Meilenstein-Animation: Ziel relativ zur Mitte in px, Drehung in Grad, Verzögerung in ms. */
export interface MilestoneBurstPiece {
  dx: number;
  dy: number;
  rotate: number;
  size: number;
  shape: 'dot' | 'bar';
  color: string;
  delay: number;
}

const BURST_COLORS = ['#922C57', '#D99F1A', '#E7B8CB', '#4A7C82', '#C2477A'];

/**
 * Konfetti um den Pokal, wenn eine Fahrt einen Meilenstein überquert (Web `MilestoneBurst`, App
 * dasselbe mit `Animated`). Festgelegt statt zufällig: Web und App zeigen dieselbe Figur, und ein
 * Screenshot im Design-Lab sieht jedes Mal gleich aus. Die Winkel stehen im goldenen Winkel
 * zueinander, damit keine Lücke und keine Häufung entsteht; die Weite schwankt in drei Ringen.
 */
export function milestoneBurst(count = 18, radius = 74): MilestoneBurstPiece[] {
  const golden = 137.508;
  return Array.from({ length: count }, (_, i) => {
    const angle = ((i * golden) % 360) * (Math.PI / 180);
    const reach = radius * [1, 0.72, 0.86][i % 3];
    return {
      dx: Math.round(Math.cos(angle) * reach),
      // Leicht nach oben gezogen: Konfetti steigt erst und fällt dann (die Fallstrecke legt die Plattform drauf).
      dy: Math.round(Math.sin(angle) * reach * 0.8 - 10),
      rotate: ((i * 67) % 300) - 150,
      size: [7, 5, 9, 6][i % 4],
      shape: i % 3 === 1 ? 'dot' : 'bar',
      color: BURST_COLORS[i % BURST_COLORS.length],
      delay: (i % 4) * 35,
    };
  });
}

/**
 * Wie weit die Linie der Meilenstein-Reihe gefüllt ist, in Prozent der Strecke vom ersten bis zum
 * letzten Punkt. Jeder Abschnitt zählt gleich viel, egal wie viele Kilometer er umfasst: die Punkte
 * stehen gleichmäßig verteilt, und 250 bis 500 km sollen sich so lang anfühlen, wie sie aussehen.
 * Vor dem ersten Meilenstein bleibt die Linie leer.
 */
export function milestoneTrackPercent(km: number): number {
  const ms = CHALLENGE_MILESTONES;
  if (km <= ms[0]) return 0;
  for (let i = 0; i < ms.length - 1; i++) {
    if (km < ms[i + 1]) return ((i + (km - ms[i]) / (ms[i + 1] - ms[i])) / (ms.length - 1)) * 100;
  }
  return 100;
}

/** Fortschritt in Prozent, auf 0 bis 100 begrenzt. */
export function progressPercent(value: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.max(0, Math.min(100, (value / goal) * 100));
}

export type ChallengePhase = 'before' | 'running' | 'after';

/** Vor, während oder nach dem Wertungszeitraum; `today` als ISO-Datum. */
export function challengePhase(summary: Pick<ChallengeSummary, 'startsOn' | 'endsOn'>, today: string): ChallengePhase {
  if (today < summary.startsOn) return 'before';
  if (today > summary.endsOn) return 'after';
  return 'running';
}

/** Ganze Tage von `from` bis `to` (ISO-Daten). */
export function daysBetween(from: string, to: string): number {
  const ms = Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z');
  return Math.round(ms / 86_400_000);
}

/** Kilometer deutsch formatiert: „1.245“, unter 100 mit einer Nachkommastelle, wenn nötig. */
export function formatKm(km: number): string {
  const digits = km < 100 && km % 1 !== 0 ? 1 : 0;
  return km.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Große Summen kurz: „623.450“ bleibt, ab einer Million „1,2 Mio.“ */
export function formatKmShort(km: number): string {
  if (km >= 1_000_000) return `${(km / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio.`;
  return formatKm(Math.round(km));
}

/** Entfernung Erde–Mond, mittlerer Abstand. Seit dem 25.10.2026 das gemeinsame Ziel (vorher 1 Mio. km, zu viel). */
export const CHALLENGE_MOON_KM = 384_400;

/**
 * Wie das gemeinsame Ziel heißt (Web und App). Ist es die Mondentfernung, sagt die Seite das, statt
 * eine nackte Zahl zu nennen: 384.400 km merkt sich niemand, „bis zum Mond“ schon.
 */
export function challengeGoalModel(summary: Pick<ChallengeSummary, 'goalKm' | 'totalKm' | 'startsOn' | 'endsOn'>, today: string) {
  const moon = summary.goalKm === CHALLENGE_MOON_KM;
  const after = challengePhase(summary, today) === 'after';
  const reached = summary.totalKm >= summary.goalKm;
  return {
    moon,
    /** Beschriftung über dem Balken. */
    label: after && !(moon && reached) ? 'Gemeinsam gefahren' : moon ? 'Gemeinsam bis zum Mond' : 'Gemeinsames Ziel',
    /** Überschrift im Hinweis auf der Startseite: „Gemeinsam bis zum Mond: 384.400 km“. */
    // Geschütztes Leerzeichen: „km“ stand auf dem Handy sonst allein in der letzten Zeile.
    headline: `${moon ? 'Gemeinsam bis zum Mond' : 'Gemeinsames Ziel'}: ${formatKmShort(summary.goalKm)}\u00a0km`,
    /** Spielregeln. */
    rule: moon
      ? `Alle zusammen fahren ${formatKm(summary.goalKm)} km, so weit ist es von der Erde bis zum Mond.`
      : `Alle zusammen fahren ${formatKm(summary.goalKm)} km.`,
  };
}

const PROOF_HOSTS: { pattern: RegExp; label: string }[] = [
  { pattern: /(^|\.)strava\.(com|app\.link)$/, label: 'Strava' },
  { pattern: /(^|\.)garmin\.com$/, label: 'Garmin Connect' },
  { pattern: /(^|\.)komoot\.(com|de)$/, label: 'Komoot' },
  { pattern: /(^|\.)wahoofitness\.com$/, label: 'Wahoo' },
  { pattern: /(^|\.)zwift\.com$/, label: 'Zwift' },
  { pattern: /(^|\.)trainingpeaks\.com$/, label: 'TrainingPeaks' },
  { pattern: /(^|\.)intervals\.icu$/, label: 'intervals.icu' },
  { pattern: /(^|\.)polar\.com$/, label: 'Polar' },
  { pattern: /(^|\.)suunto\.com$/, label: 'Suunto' },
];

/**
 * Wie der Nachweis-Link heißt: bekannte Dienste mit Namen, sonst die Domain. `null` heißt
 * „kein gültiger Link“ (kein http(s), keine Domain).
 */
export function proofLinkLabel(url?: string | null): string | null {
  if (!url) return null;
  const m = /^https?:\/\/([^/?#:]+)/i.exec(url.trim());
  if (!m || !m[1].includes('.')) return null;
  const host = m[1].toLowerCase().replace(/^www\./, '');
  const known = PROOF_HOSTS.find(h => h.pattern.test(host));
  return known ? known.label : host;
}

/**
 * Kilometer aus dem Formular lesen, deutsch und englisch geschrieben: „48“, „32,5“, „32.5“.
 * Ein Punkt vor drei Ziffern ist ein Tausenderpunkt („1.250“ sind 1.250 km, nicht 1,25), mehr
 * als eine Nachkommastelle wird nicht still gerundet, sondern genannt. Der Server rundet auf
 * eine Stelle; wer „12,345“ tippt, soll das vorher sehen.
 */
export function parseKm(input: string): { km: number | null; error: string | null } {
  let s = input.trim().replace(/\s/g, '');
  if (!s) return { km: null, error: null };
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  if (!/^\d+(\.\d+)?$/.test(s)) return { km: null, error: 'Bitte eine Zahl, zum Beispiel 48 oder 32,5.' };
  const decimals = s.split('.')[1];
  if (decimals && decimals.length > 1) return { km: null, error: 'Höchstens eine Nachkommastelle.' };
  return { km: Number(s), error: null };
}

export interface ChallengeRideDraft {
  rideDate: string;
  km: string;
  bikeType: ChallengeBikeType | null;
  proofUrl: string;
}

/**
 * Prüft ein Formular „Kilometer eintragen“, bevor es abgeschickt wird. Dieselben Grenzen prüft
 * der Server noch einmal; die Tagessumme kennt nur er vollständig, `kmSameDay` ist der Stand
 * der eigenen Liste.
 */
export function validateRideDraft(
  draft: ChallengeRideDraft,
  season: Pick<ChallengeSummary, 'startsOn' | 'endsOn'>,
  today: string,
  kmSameDay = 0,
): Partial<Record<keyof ChallengeRideDraft, string>> {
  const errors: Partial<Record<keyof ChallengeRideDraft, string>> = {};
  if (!draft.rideDate) errors.rideDate = 'Wann bist du gefahren?';
  else if (draft.rideDate > today) errors.rideDate = 'Das Datum liegt in der Zukunft.';
  else if (draft.rideDate < season.startsOn || draft.rideDate > season.endsOn) {
    errors.rideDate = 'Gezählt wird nur im Zeitraum der Challenge.';
  }
  const parsed = parseKm(draft.km);
  if (!draft.km.trim()) errors.km = 'Wie weit bist du gefahren?';
  else if (parsed.error) errors.km = parsed.error;
  else if (parsed.km == null || parsed.km < 1) errors.km = 'Mindestens 1 km.';
  else if (parsed.km + kmSameDay > CHALLENGE_MAX_KM_PER_DAY) {
    errors.km = `Mehr als ${CHALLENGE_MAX_KM_PER_DAY} km an einem Tag? Schreib uns, dann tragen wir es ein.`;
  }
  if (!draft.bikeType) errors.bikeType = 'Womit bist du gefahren?';
  if (draft.proofUrl.trim() && !proofLinkLabel(draft.proofUrl)) {
    errors.proofUrl = 'Das ist kein Link. Er beginnt mit https://';
  }
  return errors;
}

/**
 * Vorschlag für den Anzeigenamen beim Mitmachen: der richtige Name, sonst der Benutzername. Der
 * Teilnehmer sieht ihn im Formular und kann ihn ändern. Automatisch erzeugte Benutzernamen
 * (`lena_3f9a1c`, aus dem Code-Login) verlieren ihre Zufallsendung.
 */
export function challengeDisplayNameFor(user: Pick<User, 'firstName' | 'lastName' | 'username'> | null | undefined): string {
  if (!user) return '';
  const full = [user.firstName, user.lastName].map(s => (s || '').trim()).filter(Boolean).join(' ');
  if (full) return full.slice(0, 40);
  return (user.username || '').replace(/_[0-9a-f]{6}$/, '').slice(0, 40);
}

/** ISO-Datum um `n` Tage verschoben (UTC, damit keine Zeitzone einen Tag verschluckt). */
export function shiftDay(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * `consent` ist kein Kästchen mehr (23.10.2026): die öffentliche Rangliste ist Teil der Teilnahme,
 * der Hinweis steht über dem Knopf. Ein vorab angehaktes Kästchen wäre keine wirksame
 * Einwilligung gewesen. Wer „Mitmachen“ drückt, schickt `consent: true`.
 */
export interface ChallengeJoinDraft { displayName: string; bundesland: string; consent: boolean }

/** Prüft „Mitmachen“. Texte hier, damit Web und App dasselbe sagen. */
export function validateJoinDraft(draft: ChallengeJoinDraft): Partial<Record<keyof ChallengeJoinDraft, string>> {
  const errors: Partial<Record<keyof ChallengeJoinDraft, string>> = {};
  if (draft.displayName.trim().length < 2) errors.displayName = 'Wie sollen wir dich in der Rangliste nennen?';
  if (!draft.bundesland) errors.bundesland = 'Für welche Region fährst du?';
  return errors;
}

/** Prüft den Anzeigenamen beim Umbenennen; dieselbe Grenze wie das Backend. */
export function validateDisplayName(name: string): string | null {
  const n = name.trim().replace(/\s+/g, ' ');
  if (n.length < 2) return 'Wie sollen wir dich in der Rangliste nennen?';
  if (n.length > 40) return 'Höchstens 40 Zeichen.';
  return null;
}

/** Hinweise unter den Feldern von „Mitmachen“ und „Kilometer eintragen“, gemeinsam für Web und App. */
export const CHALLENGE_TEXTS = {
  rankingRules: 'Einzel, Teams, Bundesländer und Länder sammeln Kilometer. Teams sind beliebig groß.',
  displayNameHint: 'Den Namen kannst du ändern, ein Spitzname geht auch.',
  regionHint: 'Deine Kilometer zählen auch für dein Bundesland und dein Land. Die Region steht danach in deinem Profil.',
  consent: 'Mit dem Mitmachen erscheinen dein Anzeigename, deine Region und deine Kilometer in der öffentlichen Rangliste.',
  proofHint: 'Strava, Garmin, Komoot oder Zwift. Sieht nur die Redaktion, falls eine Fahrt geprüft werden muss.',
  /** Abruf gescheitert: leer ist nicht gleich kaputt, deshalb ein eigener Satz statt „noch niemand“. */
  boardUnavailable: 'Die Rangliste ist gerade nicht erreichbar. Versuch es gleich noch einmal.',
  pageUnavailable: 'Die Winterchallenge ist gerade nicht erreichbar. Versuch es gleich noch einmal.',
  noSeason: 'Gerade läuft keine Winterchallenge. Die nächste beginnt im November.',
} as const;

/**
 * Leerzustände mit dem Starttag der Saison: die Saison ist eine Zeile in der Datenbank, kein
 * Code, deshalb steht „1. November“ nicht fest im Text.
 */
export function challengeEmptyTexts(summary: Pick<ChallengeSummary, 'startsOn'>) {
  const start = dayMonthLong(summary.startsOn);
  return {
    noRides: `Noch keine Fahrt eingetragen. Jeder Kilometer ab dem ${start} zählt, auch nachträglich.`,
    /** Vor dem Start: eintragen geht noch nicht, „noch keine Fahrt“ wäre selbstverständlich (Phil, 25.10.2026). */
    noRidesBefore: `Ab dem ${start} kannst du hier deine Kilometer eintragen.`,
    emptyBoard: `Noch niemand in der Wertung. Wer am ${start} die erste Fahrt einträgt, steht hier oben.`,
  };
}

/** „Start heute“, „Start morgen“, „Start in 5 Tagen“. Einmal für Kopf, Hinweis und Profil (Web und App). */
export function startInLabel(summary: Pick<ChallengeSummary, 'startsOn'>, today: string): string {
  const days = daysBetween(today, summary.startsOn);
  if (days <= 0) return 'Start heute';
  if (days === 1) return 'Start morgen';
  return `Start in ${days} Tagen`;
}

/** „01.11.2026 bis 31.03.2027“. */
export function seasonRangeLabel(summary: Pick<ChallengeSummary, 'startsOn' | 'endsOn'>): string {
  return `${formatDayShort(summary.startsOn)} bis ${formatDayShort(summary.endsOn)}`;
}

/**
 * Die Spielregeln, einmal für Web und App (bis zum 26.10.2026 standen sie zweimal mit
 * abweichendem Wortlaut: „eine Woche“ hier, „sieben Tage“ dort).
 */
export function challengeRules(summary: Pick<ChallengeSummary, 'startsOn' | 'endsOn' | 'goalKm' | 'totalKm'>): { title: string; text: string }[] {
  return [
    { title: 'Zeitraum', text: `${seasonRangeLabel(summary)}. Fahrten lassen sich nachtragen, bis eine Woche nach dem Ende.` },
    { title: 'Gemeinsames Ziel', text: challengeGoalModel(summary, summary.startsOn).rule },
    { title: 'Was zählt', text: 'Jeder Kilometer auf dem Rad: Rennrad, Gravel, MTB, Touren und Indoor auf der Rolle.' },
    { title: 'Wertungen', text: CHALLENGE_TEXTS.rankingRules },
    { title: 'Fair Play', text: 'Trag nur ein, was du gefahren bist. Ein Link zu Strava, Garmin oder Komoot hilft uns, wenn eine Fahrt geprüft werden muss. Den Link sieht nur die Redaktion.' },
    { title: 'Am Ende', text: 'Für deine Kilometer gibt es eine Auszeichnung, die dauerhaft in deinem Profil bleibt.' },
  ];
}

/** Name einer Ranglistenzeile zur Anzeige: Regionen mit Umlauten, Länder ausgeschrieben. */
export function boardRowName(kind: ChallengeBoardKind, row: ChallengeBoardRow): string {
  if (kind === 'riders' && row.teamAbbreviation) return `[${row.teamAbbreviation}] ${row.name}`;
  if (kind === 'regions') return displayBundesland(row.name);
  if (kind === 'countries') return COUNTRY_LABELS[row.name as CountryCode] ?? row.name;
  return row.name;
}

/** Zweite Zeile: Region des Fahrers, sonst die Zahl der Teilnehmenden (bei Teams mit Verein). */
export function boardRowDetail(kind: ChallengeBoardKind, row: ChallengeBoardRow): string | null {
  if (kind === 'riders') return row.detail ? displayBundesland(row.detail) : null;
  if (row.members == null) return row.detail ?? null;
  const members = `${row.members.toLocaleString('de-DE')} ${row.members === 1 ? 'Teilnehmer' : 'Teilnehmende'}`;
  return kind === 'teams' && row.detail ? `${members} · ${row.detail}` : members;
}


/** Ab hier steht ein Teilnehmer in der Liste „Auffällig“ (gespiegelt im Backend). */
export const CHALLENGE_FLAG_WEEK_KM = 1500;
export const CHALLENGE_FLAG_DAY_AVERAGE_KM = 150;

export const CHALLENGE_FLAG_REASON_LABELS: Record<'WEEK_KM' | 'DAY_AVERAGE' | 'REPORTED', string> = {
  WEEK_KM: `Über ${CHALLENGE_FLAG_WEEK_KM.toLocaleString('de-DE')} km in 7 Tagen`,
  DAY_AVERAGE: `Über ${CHALLENGE_FLAG_DAY_AVERAGE_KM} km am Tag im Schnitt`,
  REPORTED: 'Gemeldet',
};

/** Nachträge einschließlich des siebten Tages nach Saisonende, in deutscher Zeit. */
export function challengeEditable(season: Pick<ChallengeSummary, 'startsOn' | 'endsOn'>, today: string): boolean {
  return today >= season.startsOn && today <= shiftDay(season.endsOn, 7);
}

export function challengeToday(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

/** Nur bekannte Teamrouten zulassen; Rücksprünge dürfen nie auf fremde Seiten führen. */
export function challengeTeamReturnPath(slug?: string | null, code?: string | null): string | null {
  if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || slug.length > 80) return null;
  if (!code || !/^[a-z0-9]{1,12}$/i.test(code)) return null;
  return `/winterchallenge/team/${slug}?code=${encodeURIComponent(code)}`;
}

/**
 * Was jemand beim Mitmachen von einer Teamseite mitbringt: einen Einladungslink (`?code=`, danach
 * gleich im Team) oder eine Beitrittsanfrage (`?anfrage=1`, danach ist sie gestellt). `query`
 * hängt die Absicht an `/winterchallenge?mitmachen=1` an, damit sie die Anmeldung übersteht.
 */
export interface ChallengeTeamIntent { slug: string; code: string | null; returnPath: string; query: string }

export function challengeTeamIntent(slug?: string | null, code?: string | null, anfrage?: string | null): ChallengeTeamIntent | null {
  const invite = challengeTeamReturnPath(slug, code);
  if (invite) return { slug: slug!, code: code!, returnPath: invite, query: `&team=${encodeURIComponent(slug!)}&code=${encodeURIComponent(code!)}` };
  if (anfrage !== '1' || !challengeTeamReturnPath(slug, 'x')) return null;
  return { slug: slug!, code: null, returnPath: `/winterchallenge/team/${slug}`, query: `&team=${encodeURIComponent(slug!)}&anfrage=1` };
}

/**
 * Was die Teamseite jemandem anbietet, der nicht im Team ist. Ohne Einladungslink kann man seit
 * dem 27.10.2026 anfragen (Romans Wunsch); der Gründer nimmt an oder lehnt ab. Anfragen an mehrere
 * Teams gehen gleichzeitig, die zuerst angenommene gewinnt (`docs/bereiche/winterchallenge.md`).
 */
export type TeamJoinMode = 'member' | 'closed' | 'otherTeam' | 'invite' | 'request' | 'pending' | 'declined';

export function teamJoinMode(
  team: Pick<ChallengeTeam, 'isMember' | 'joinClosed' | 'myRequest'>,
  opts: { inviteCode?: string | null; currentTeamName?: string | null },
): TeamJoinMode {
  if (team.isMember) return 'member';
  if (team.joinClosed) return 'closed';
  if (opts.currentTeamName) return 'otherTeam';
  if (opts.inviteCode) return 'invite';
  if (team.myRequest === 'PENDING') return 'pending';
  if (team.myRequest === 'DECLINED') return 'declined';
  return 'request';
}

/** Texte rund um Beitrittsanfragen, für Web und App. */
export const TEAM_REQUEST_TEXTS = {
  request: 'Beitritt anfragen',
  requestAndJoin: 'Mitmachen und anfragen',
  requestHint: 'Kein Einladungslink? Frag an, ob du mitfahren kannst. Wer das Team gegründet hat, entscheidet.',
  pending: (teamName: string) => `Deine Anfrage ist gestellt. Sobald ${teamName} sie annimmt, fährst du mit.`,
  withdraw: 'Anfrage zurückziehen',
  declined: 'Das Team hat deine Anfrage abgelehnt. Frag gern bei einem anderen Team an.',
  otherTeam: (teamName: string) => `Du fährst schon für ${teamName}. Ein Wechsel ist nicht möglich.`,
  sent: (teamName: string) => `Anfrage an ${teamName} gestellt`,
  sentAfterJoin: (teamName: string) => `Du bist dabei. Deine Anfrage an ${teamName} ist gestellt.`,
  listTitle: 'Beitrittsanfragen',
  listHint: 'Wer hier anfragt, hat keinen Einladungslink. Nimmst du an, zählen die bisherigen Kilometer gleich fürs Team.',
  accept: 'Annehmen',
  decline: 'Ablehnen',
  accepted: (name: string) => `${name} fährt jetzt im Team`,
  declinedToast: 'Anfrage abgelehnt',
  /** Auf `/winterchallenge`, solange man in keinem Team ist. */
  waiting: (n: number) => (n === 1 ? 'Eine Anfrage wartet auf Antwort' : `${n} Anfragen warten auf Antwort`),
  /** Auf `/winterchallenge` für den Gründer. */
  openToMyTeam: (n: number) => (n === 1 ? '1 Beitrittsanfrage' : `${n} Beitrittsanfragen`),
} as const;

/**
 * Zurück vom Mitmachen über einen Einladungslink (`?team=…&code=…`): wer auf „Beitreten“ geklickt
 * hat, ist danach im Team, ohne ein zweites Mal zu klicken (Phil, 25.10.2026: Anmelden, Kontofrage,
 * Mitmachen und dann noch einmal „Beitreten“ war ein Schritt zu viel). Wer schon für ein anderes
 * Team fährt, landet auf der Teamseite; dort steht, dass ein Wechsel nicht geht.
 */
export function teamInviteNextStep(me: Pick<ChallengeMe, 'team'>, slug: string): 'join' | 'already' | 'otherTeam' {
  if (!me.team) return 'join';
  return me.team.slug === slug ? 'already' : 'otherTeam';
}

/** „1. November“: Tag und Monat ausgeschrieben, für Fließtext; geschütztes Leerzeichen, damit „1.“ nicht allein am Zeilenende steht. */
function dayMonthLong(iso: string): string {
  return formatDayMonthLong(iso).replace(' ', '\u00A0');
}

/**
 * Was jemand liest, der über einen Einladungslink auf der Teamseite landet und noch nicht
 * mitmacht (Web und App). Ohne das sah er Teamname, Kilometer und „Team beitreten“, aber nicht,
 * worum es geht (Phil, 25.10.2026). Zählt auf, statt zu werben.
 */
export function teamInviteIntro(summary: ChallengeSummary, teamName: string) {
  return {
    title: summary.title,
    lead: `Kilometer sammeln vom ${dayMonthLong(summary.startsOn)} bis ${dayMonthLong(summary.endsOn)}.`,
    points: [
      'Jede Fahrt zählt, draußen oder auf der Rolle.',
      'Nach jeder Fahrt trägst du die Kilometer ein.',
      `Deine Kilometer zählen für dich und für ${teamName}.`,
    ],
    people: summary.participants > 0 ? `${summary.participants.toLocaleString('de-DE')} fahren schon mit.` : null,
    join: 'Mitmachen und beitreten',
  };
}

/** Kalenderwoche (Montag bis Sonntag) und Monat; mehrere Fahrten pro Tag zählen als ein aktiver Tag. */
export function challengeActivity(rides: ChallengeRide[], today: string, endsOn: string) {
  const day = today > endsOn ? endsOn : today;
  const weekday = new Date(day + 'T00:00:00Z').getUTCDay();
  const monday = shiftDay(day, -((weekday + 6) % 7));
  const month = day.slice(0, 7);
  const weekRides = rides.filter(r => r.rideDate >= monday && r.rideDate <= day);
  const monthRides = rides.filter(r => r.rideDate.slice(0, 7) === month && r.rideDate <= day);
  const total = rides.reduce((sum, r) => sum + r.km, 0);
  const indoor = rides.filter(r => r.bikeType === 'INDOOR').reduce((sum, r) => sum + r.km, 0);
  const round = (km: number) => Math.round(km * 10) / 10;
  return {
    weekKm: round(weekRides.reduce((sum, r) => sum + r.km, 0)),
    weekDays: new Set(weekRides.map(r => r.rideDate)).size,
    monthKm: round(monthRides.reduce((sum, r) => sum + r.km, 0)),
    monthLabel: formatMonthLong(day),
    weekLabel: today > endsOn ? 'Letzte Saisonwoche' : 'Diese Woche',
    indoorKm: round(indoor), indoorPercent: total > 0 ? Math.round(indoor / total * 100) : 0,
  };
}

/** Erster, mittlerer (bei gerader Länge der rechte) und letzter Buchstabe, ohne Leer-/Sonderzeichen. */
export function suggestTeamAbbreviation(name: string): string {
  const letters = Array.from(normalizeTeamAbbreviation(name)).filter(c => /[\p{L}\p{N}]/u.test(c));
  if (!letters.length) return '';
  return letters[0] + letters[Math.floor(letters.length / 2)] + letters[letters.length - 1];
}

export function normalizeTeamAbbreviation(value: string): string {
  return value.trim().normalize('NFC').toLocaleUpperCase('de-DE');
}

export function teamAbbreviationError(value: string): string | null {
  return /^[^\s\p{C}]{3}$/u.test(value) ? null : 'Genau drei Zeichen, ohne Leerzeichen.';
}

/**
 * Die Kachel im Profil (Web und App), nur für Teilnehmer: eigene Kilometer, Platz und der Weg zum
 * nächsten Meilenstein. Vor dem Start steht dort, wann es losgeht, statt eines leeren Balkens.
 */
export function profileChallengeModel(me: ChallengeMe, summary: ChallengeSummary, today: string) {
  const phase = challengePhase(summary, today);
  const next = nextMilestone(me.km);
  const parts: string[] = [];
  if (me.rank != null) parts.push(`Platz ${me.rank.toLocaleString('de-DE')}`);
  if (phase === 'before') {
    parts.push(startInLabel(summary, today));
  } else if (phase === 'after') {
    if (me.rank == null) parts.push('Saison beendet');
  } else if (next && me.km === 0) {
    parts.push(`Erster Meilenstein bei ${formatKm(next.target)} km`);
  } else if (next) {
    parts.push(`Noch ${formatKm(Math.round(next.missing * 10) / 10)} km bis ${formatKm(next.target)} km`);
  } else {
    parts.push('Alle Meilensteine erreicht');
  }
  const editable = challengeEditable(summary, today);
  return {
    title: summary.title,
    km: formatKm(Math.round(me.km * 10) / 10),
    detail: parts.join(' · '),
    /** Fortschritt zum nächsten Meilenstein, nur während der Saison. */
    percent: phase !== 'running' ? null : next ? progressPercent(me.km - next.previous, next.target - next.previous) : 100,
    /** Eintragen öffnet das Schnellfenster (`QuickRideDialog` bzw. das Blatt in der App). */
    action: editable ? 'Kilometer eintragen' : phase === 'after' ? 'Ergebnisse ansehen' : 'Zur Challenge',
    addRide: editable,
  };
}

/**
 * Der Hinweis auf der Startseite (Web) bzw. auf „Entdecken“ (App) für jemanden, der schon
 * mitmacht: statt „Mitmachen“ die eigenen Kilometer und der Weg zum Eintragen. Dieselben Angaben
 * wie die Kachel im Profil, nur mit eigener Überschrift.
 */
export function participantTeaserModel(me: ChallengeMe, summary: ChallengeSummary, today: string) {
  const m = profileChallengeModel(me, summary, today);
  const phase = challengePhase(summary, today);
  const headline = phase === 'before' || (phase === 'running' && me.km === 0) ? 'Du bist dabei.'
    : phase === 'after' ? `Deine Saison: ${m.km} km.`
    : `Du bist bei ${m.km} km.`;
  return { ...m, headline, href: m.addRide ? '/winterchallenge?eintragen=1' : '/winterchallenge' };
}

/**
 * Was nach dem Eintragen im Schnellfenster steht (Startseite, Profil; Web und App): wie viel dazu
 * kam, der neue Stand und ob dabei ein Meilenstein fiel. Der Balken läuft vom alten zum neuen
 * Stand; fiel ein Meilenstein, füllt er den erreichten Abschnitt bis zum Ende, statt im neuen
 * fast leer anzufangen.
 */
export function rideSavedModel(before: ChallengeMe, after: ChallengeMe, summary: ChallengeSummary, today: string) {
  const added = Math.round((after.km - before.km) * 10) / 10;
  const milestone = crossedMilestone(before.km, after.km);
  const m = profileChallengeModel(after, summary, today);
  const segment = milestone != null ? nextMilestone(before.km) : nextMilestone(after.km);
  const from = segment ? progressPercent(before.km - segment.previous, segment.target - segment.previous) : 0;
  return {
    added,
    addedLabel: `${added >= 0 ? '+' : ''}${formatKm(added)} km`,
    headline: milestone != null ? `${formatKm(milestone)} km geschafft.` : 'Fahrt eingetragen.',
    milestone,
    km: m.km,
    detail: m.detail,
    percentBefore: from,
    percentAfter: milestone != null ? 100 : m.percent ?? 100,
  };
}
